<template>
  <div class="operation-settings">
    <HeaderBar :modifiers="['transparent', 'form']">
      <div class="header-bar__title typo-title">
        <span v-if="shaftId">{{ $t('shafts.edit_shaft') }}</span>
        <span v-else>{{ $t('shafts.new_shaft') }}</span>
      </div>

      <template #right>
        <a
          class="button"
          @click="$router.back()"
        >
          {{ $t('general.cancel') }}
        </a>
        <FormSubmitButton
          :isSaving="saving"
          @click="save"
        />
      </template>
    </HeaderBar>

    <div class="form-layout form-layout--border-radius-top form-layout--border-radius-bottom">
      <div class="form-layout__content">
        <div class="form-layout__row form-layout__row--space-between">
          <div
            aria-label="Shaft name"
            class="form-layout__column form-layout__column--large"
          >
            <FormRow
              :errors="errors?.shaft?.name"
              :label="$t('operations.operation_name_label_shaft')"
              labelFor="shaft_name"
              :modifiers="['no-padding']"
            >
              <div class="form-row__input form-row__input--no-peek">
                <div :class="{ input: true, 'input--error': errors?.shaft?.name }">
                  <input
                    id="shaft_name"
                    v-model="shaft.name"
                  >
                </div>
              </div>
            </FormRow>
          </div>

          <div class="form-layout__column form-layout__column--small">
            <FormRow
              :errors="errors?.shaft?.mine"
              :label="$t('shafts.select_mine')"
              labelFor="shaft_mine"
              :modifiers="['no-padding']"
            >
              <div class="form-row__input form-row__input--no-peek">
                <MineSelect
                  id="shaft_mine"
                  v-model="shaft.mine"
                  :class="{ 'input--error': errors?.shaft?.mine }"
                  :mines="mines"
                />
              </div>
            </FormRow>
          </div>
        </div>

        <div class="form-layout__row">
          <div class="form-layout__column form-layout__column--full-width">
            <FormRow
              v-if="shaft.mine && shaft.mine.isNew"
              :errors="errors?.shaft?.mine?.name"
              :label="$t('shafts.operation_new_mine_label')"
              labelFor="new_mine_name"
              :modifiers="['no-padding']"
            >
              <div class="form-row__input form-row__input--no-peek">
                <div :class="{ input: true, 'input--error': errors?.shaft?.mine?.name }">
                  <input
                    id="new_mine_name"
                    v-model="shaft.mine.name"
                  >
                </div>
              </div>
            </FormRow>
          </div>
        </div>

        <div class="form-layout__row--mobile form-layout__row--space-between">
          <div class="form-layout__column form-layout__column--mobile form-layout__column--small">
            <FormRow
              :errors="errors?.shaft?.number_of_sets"
              :label="$t('shafts.number_of_sets')"
              labelFor="number_of_sets"
              :modifiers="['no-padding']"
            >
              <div class="form-row__input form-row__input--no-peek">
                <div :class="{ input: true, 'input--error': errors?.shaft?.number_of_sets }">
                  <input
                    id="number_of_sets"
                    v-model="shaft.number_of_sets"
                    @blur="validateInput($event, initialNumberOfSets, 'number_of_sets')"
                  >
                </div>
              </div>
            </FormRow>
          </div>

          <div class="form-layout__column form-layout__column--mobile form-layout__column--small">
            <FormRow
              :errors="errors?.shaft?.set_spacing"
              :label="$t('shafts.set_spacing')"
              labelFor="set_spacing"
              :modifiers="['no-padding']"
            >
              <div class="form-row__input form-row__input--no-peek">
                <div :class="{ input: true, 'input--error': errors?.shaft?.set_spacing }">
                  <input
                    id="set_spacing"
                    v-model="shaft.set_spacing"
                    @blur="validateInput($event, 1, 'set_spacing')"
                  >
                </div>
              </div>
            </FormRow>
          </div>

          <div
            :class="{'form-layout__column form-layout__column--small form-layout__column--flex-end show-for-medium': true, 'form-layout__column--margin-bottom': errors?.shaft?.number_of_sets || errors?.shaft?.set_spacing}"
          >
            <span class="italic">{{ `${$t('shafts.shaft_depth_label')} = ${shaftDepth}m` }}</span>
          </div>
        </div>

        <div class="form-layout__column form-layout__column--small form-layout__column--flex-end hide-for-medium">
          <span class="italic">{{ `${$t('shafts.shaft_depth_label')} = ${shaftDepth}m` }}</span>
        </div>

        <div class="form-layout__row form-layout__row--space-between">
          <div class="form-layout__column">
            <FormRow
              :errors="errors?.shaft?.shape && !shaft?.shape ? errors?.shaft?.shape : []"
              :label="$t('shafts.select_shaft')"
              labelFor="shaft_shape"
              :modifiers="['no-padding']"
            >
              <div class="form-row__input form-row__input--no-peek">
                <ShaftShape
                  id="shaft_shape"
                  v-model="shaft.shape"
                  :class="{ 'shape--error': errors?.shaft?.shape && !shaft?.shape }"
                  :iconList="shapeIcons"
                />
              </div>
            </FormRow>
          </div>
        </div>

        <div class="form-layout__row form-layout__row--space-between">
          <div class="form-layout__column form-layout__column--small">
            <FormRow
              :errors="errors?.shaft?.service_speed"
              :label="$t('shafts.service_speed')"
              labelFor="service_speed"
              :modifiers="['no-padding']"
            >
              <div class="form-row__input form-row__input--no-peek">
                <div :class="{ input: true, 'input--error': errors?.shaft?.service_speed }">
                  <input
                    id="service_speed"
                    v-model="shaft.service_speed"
                  >
                </div>
              </div>
            </FormRow>
          </div>
          <div class="form-layout__column form-layout__column--small">
            <FormRow
              :errors="errors?.shaft?.cage_speed"
              :label="$t('shafts.cage_speed')"
              labelFor="cage_speed"
              :modifiers="['no-padding']"
            >
              <div class="form-row__input form-row__input--no-peek">
                <div :class="{ input: true, 'input--error': errors?.shaft?.cage_speed }">
                  <input
                    id="cage_speed"
                    v-model="shaft.cage_speed"
                  >
                </div>
              </div>
            </FormRow>
          </div>
          <div class="form-layout__column form-layout__column--small">
            <FormRow
              :errors="errors?.shaft?.skip_speed"
              :label="$t('shafts.skip_speed')"
              labelFor="skip_speed"
              :modifiers="['no-padding']"
            >
              <div class="form-row__input form-row__input--no-peek">
                <div :class="{ input: true, 'input--error': errors?.shaft?.skip_speed }">
                  <input
                    id="skip_speed"
                    v-model="shaft.skip_speed"
                  >
                </div>
              </div>
            </FormRow>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';

import useAjax from '@/functions/use_ajax';
import I18n from 'i18n-js';
import truncateNumber from '@/functions/simm/truncate_number';

import HeaderBar from '@/components/generic/header_bar.vue';
import FormRow from '@/components/form/row.vue';
import FormSubmitButton from '@/components/form/form_submit_button.vue';
import MineSelect from '@/components/shafts/operations/mine_select.vue';
import ShaftShape from '@/components/form/shaft_shape.vue';

const props = defineProps({
  organisationId: { type: String, required: true },
  shaftId: { type: String, default: null },
});

const router = useRouter();
const ajax = useAjax();

const shaft = reactive(createNewShaft());
const errors = ref(null);
const saving = ref(false);
const mines = ref(null);
const initialNumberOfSets = ref(null);

/**
 * Shape list
 */
const shapeIcons = ref(
  [
    'circle',
    'circles',
    'rectangle',
    'squircle',
  ]
);

function validateInput(event, limit, key) {
  const newValue = parseFloat(event.target.value);

  if (!!newValue && newValue >= limit) {
    if (errors?.value?.shaft) {
      errors.value.shaft[key] = null;
    }
  } else {
    const errorMessage = [ I18n.t('activerecord.errors.min_limit', { type: !limit ? 1 : limit }) ];

    if (errors?.value?.shaft) {
      errors.value.shaft[key] = errorMessage;
    } else {
      errors.value = {
        shaft: {
          [key]: errorMessage,
        }
      };
    }
  }
}

const shaftDepth = computed(() => {
  if (shaft.number_of_sets && shaft.set_spacing) {
    return truncateNumber(parseFloat(shaft.number_of_sets) * parseFloat(shaft.set_spacing));
  }
  return 0;
});

const shaftPath = computed(() => {
  if (props.shaftId)
    return `/organisations/${props.organisationId}/shafts/${props.shaftId}`;
  else return `/organisations/${props.organisationId}/shafts`;
});

const loadShaft = async () => {
  if (props.shaftId) {
    let { ok, data } = await ajax(
      `/organisations/${props.organisationId}/shafts/${props.shaftId}`
    );

    if (ok) {
      Object.assign(shaft, data);
      initialNumberOfSets.value = shaft.number_of_sets;
    }
  }
};

onMounted(() => {
  loadMines();
  loadShaft();
});

function createNewShaft() {
  return {
    name: '',
    mine: null,
    number_of_sets: '',
    set_spacing: '',
    service_speed: '',
    cage_speed: '',
    skip_speed: '',
    shape: '',
  };
}

async function loadMines() {
  let { ok, data } = await ajax(`/organisations/${props.organisationId}/shafts`);

  if (ok) {
    // join all mines from different divisions into one list
    const joinList = [];
    data.divisions?.forEach(item => {
      joinList.push(...item.mines);
    });

    // return only id and name from the list items
    mines.value = joinList.map(item => {
      return {
        name: item.name,
        id: item.id
      };
    });
  }
}

const formatShaftForPost = () => {
  return {
    shaft: {
      name: shaft.name,
      number_of_sets: shaft.number_of_sets,
      service_speed: shaft.service_speed,
      cage_speed: shaft.cage_speed,
      skip_speed: shaft.skip_speed,
      set_spacing: shaft.set_spacing,
      shape: shaft.shape,
      ...formatMineAttributes(),
    },
  };
};

function formatMineAttributes() {
  if (shaft.mine?.isNew) {
    return {
      mine_id: shaft.mine.id,
      new_mine: shaft.mine.name,
    };
  } else {
    return {
      mine_id: shaft.mine?.id || null,
    };
  }
}

async function save() {
  const testNumberOfSets = !errors?.value?.shaft?.number_of_sets || errors?.value?.shaft?.number_of_sets === null;
  const testSetSpacing = !errors?.value?.shaft?.set_spacing || errors?.value?.shaft?.set_spacing === null;

  if (testNumberOfSets && testSetSpacing) {
    errors.value = null;
    saving.value = true;

    let method = props.shaftId ? 'PATCH' : 'POST';
    let { ok, data } = await ajax(shaftPath.value, {
      method,
      body: formatShaftForPost(),
    });

    if (ok) {
      router.replace({ name: 'shaft', params: { shaftId: data.id } });
    } else {
      errors.value = data;
    }

    saving.value = false;
  }
}
</script>
